import { useFormContext } from 'react-hook-form';

import { S3_BUCKET_URL } from '@/constants';
import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { useAccount } from '@/hooks/useAccount';
import { webCampaign } from '@/modules/webCampaign';
import { getBiggestTurnOnCompatibilityIdByTitle } from '@/pages/Onboarding/options/BiggestTurnOnCompatibility/utils/getBiggestTurnOnCompatibility';
import { getPartyBehaviorCompatibilityIdByTitle } from '@/pages/Onboarding/options/PartyBehaviorCompatibility/utils/getPartyBehaviorCompatibility';
import { getRelationshipRoleCompatibilityIdByTitle } from '@/pages/Onboarding/options/RelationshipRoleCompatibility/utils/getRelationshipRoleCompatibility';

export interface ArchetypeData {
  title: string;
  emoji: string;
  description: string;
  paywallImage: string;
  successImage: string;
}

export const ARCHETYPES: Record<string, ArchetypeData> = {
  'The Elegant CEO': {
    title: 'The Elegant CEO',
    emoji: '💼🔥',
    description:
      "She's powerful, composed, and doesn't let just anyone in. But you? You've got her attention — now prove you can handle it.",
    paywallImage: `${S3_BUCKET_URL}/paywall/own-ai-compatibility/elegant_ceo.png`,
    successImage: `${S3_BUCKET_URL}/success-page/own-ai-compatibility/elegant_ceo.png`,
  },
  'The Fiery Seductress': {
    title: 'The Fiery Seductress',
    emoji: '🔥👠',
    description:
      "She's wild, impulsive, and craves intensity. She's already heated up... and you're the spark she needs.",
    paywallImage: `${S3_BUCKET_URL}/paywall/own-ai-compatibility/fiery_seductress.png`,
    successImage: `${S3_BUCKET_URL}/success-page/own-ai-compatibility/fiery_seductress.png`,
  },
  'The Innocent Muse': {
    title: 'The Innocent Muse',
    emoji: '🌸🎀',
    description:
      "She's shy, sweet, and has no idea how tempting she is. But now that she's yours… what will you do with her?",
    paywallImage: `${S3_BUCKET_URL}/paywall/own-ai-compatibility/innocent_muse.png`,
    successImage: `${S3_BUCKET_URL}/success-page/own-ai-compatibility/innocent_muse.png`,
  },
  'The Cozy Sweetheart': {
    title: 'The Cozy Sweetheart',
    emoji: '🧸☕',
    description:
      "She's soft, loyal, and secretly a little naughty when she trusts you. She's curled up, waiting… ready to be all yours.",
    paywallImage: `${S3_BUCKET_URL}/paywall/own-ai-compatibility/cozy_sweetheart.png`,
    successImage: `${S3_BUCKET_URL}/success-page/own-ai-compatibility/cozy_sweetheart.png`,
  },
  'The Mysterious Enchantress': {
    title: 'The Mysterious Enchantress',
    emoji: '🌙🔮',
    description:
      "She speaks in glances, touches like magic, and disappears before dawn. She's drawn to your energy — now catch her before she vanishes.",
    paywallImage: `${S3_BUCKET_URL}/paywall/own-ai-compatibility/mysterious_enchantress.png`,
    successImage: `${S3_BUCKET_URL}/success-page/own-ai-compatibility/mysterious_enchantress.png`,
  },
  'The Wild Rebel': {
    title: 'The Wild Rebel',
    emoji: '🏍️💋',
    description:
      "She breaks rules, hearts, and beds — in that order. She wants the ride of her life… and you're the one to take her there.",
    paywallImage: `${S3_BUCKET_URL}/paywall/own-ai-compatibility/wild_rebel.png`,
    successImage: `${S3_BUCKET_URL}/success-page/own-ai-compatibility/wild_rebel.png`,
  },
};

export const useArchetypeCalculation = () => {
  const { user } = useAccount();
  const form = useFormContext();

  const partyBehaviorUserData = getPartyBehaviorCompatibilityIdByTitle(
    user?.onboarding_data.partyBehavior,
  );
  const turnOnUserData = getBiggestTurnOnCompatibilityIdByTitle(
    user?.onboarding_data.biggestTurnOn,
  );
  const relationshipRoleUserData = getRelationshipRoleCompatibilityIdByTitle(
    user?.onboarding_data.relationshipRole,
  );

  const partyBehavior = form?.watch('party_behavior') || partyBehaviorUserData;
  const turnOn = form?.watch('biggest_turn_on') || turnOnUserData;
  const relationshipRole =
    form?.watch('relationship_role') || relationshipRoleUserData;

  const isV2Images = webCampaign.setup.paywallNotes.includes(
    PAYWALL_NOTES.ARCHETYPE_REAL_PHOTOS,
  );

  const getImagePath = (imageName: string, isSuccess = false) => {
    if (isV2Images) {
      return `${S3_BUCKET_URL}/paywall/own-ai-compatibility/v2/${imageName}`;
    }

    const folder = isSuccess ? 'success-page' : 'paywall';
    return `${S3_BUCKET_URL}/${folder}/own-ai-compatibility/${imageName}`;
  };

  const isElegantCEO = (): boolean => {
    return turnOn === 'confidence' && relationshipRole === 'leader';
  };

  const isInnocentMuse = (): boolean => {
    return turnOn === 'innocence' && relationshipRole === 'leader';
  };

  const isWildRebel = (): boolean => {
    const isFindOnePersonWithMysteryLeader =
      partyBehavior === 'find_one_person' &&
      turnOn === 'mystery' &&
      relationshipRole === 'leader';

    const isBeTheCenterWithMysteryFollower =
      partyBehavior === 'be_the_center' &&
      turnOn === 'mystery' &&
      relationshipRole === 'follower';

    return isFindOnePersonWithMysteryLeader || isBeTheCenterWithMysteryFollower;
  };

  const isFierySeductress = (): boolean => {
    const isConfidenceWithEqualOrFollower =
      turnOn === 'confidence' &&
      (relationshipRole === 'equal' || relationshipRole === 'follower');

    const isInnocenceWithEqualForBeTheCenter =
      turnOn === 'innocence' &&
      relationshipRole === 'equal' &&
      partyBehavior === 'be_the_center';

    return (
      isConfidenceWithEqualOrFollower || isInnocenceWithEqualForBeTheCenter
    );
  };

  const isCozySweetheart = (): boolean => {
    const isInnocenceWithEqualOrFollower =
      turnOn === 'innocence' &&
      (relationshipRole === 'equal' || relationshipRole === 'follower');

    const isNotBeTheCenterWithEqual = !(
      partyBehavior === 'be_the_center' && relationshipRole === 'equal'
    );

    return isInnocenceWithEqualOrFollower && isNotBeTheCenterWithEqual;
  };

  const isMysteriousEnchantress = (): boolean => {
    const isMysteryWithLeaderNotFindOnePerson =
      turnOn === 'mystery' &&
      relationshipRole === 'leader' &&
      partyBehavior !== 'find_one_person';

    const isMysteryWithEqualOrFollower =
      turnOn === 'mystery' &&
      (relationshipRole === 'equal' || relationshipRole === 'follower');

    return isMysteryWithLeaderNotFindOnePerson || isMysteryWithEqualOrFollower;
  };

  const createArchetypeData = (
    archetypeKey: string,
    imageName: string,
  ): ArchetypeData => {
    return {
      ...ARCHETYPES[archetypeKey],
      paywallImage: getImagePath(`${imageName}.png`),
      successImage: getImagePath(`${imageName}.png`, true),
    };
  };

  const calculateArchetype = (): ArchetypeData => {
    if (isElegantCEO()) {
      return createArchetypeData('The Elegant CEO', 'elegant_ceo');
    }

    if (isInnocentMuse()) {
      return createArchetypeData('The Innocent Muse', 'innocent_muse');
    }

    if (isWildRebel()) {
      return createArchetypeData('The Wild Rebel', 'wild_rebel');
    }

    if (isFierySeductress()) {
      return createArchetypeData('The Fiery Seductress', 'fiery_seductress');
    }

    if (isCozySweetheart()) {
      return createArchetypeData('The Cozy Sweetheart', 'cozy_sweetheart');
    }

    if (isMysteriousEnchantress()) {
      return createArchetypeData(
        'The Mysterious Enchantress',
        'mysterious_enchantress',
      );
    }

    return createArchetypeData('The Fiery Seductress', 'fiery_seductress');
  };

  const archetype = calculateArchetype();

  return {
    archetype,
    archetypes: ARCHETYPES,
  };
};
